export const CANONICAL_ORIGIN = 'https://www.localingos.com';

export const SUPPORTED_SEO_LOCALES = {
  en: { i18nKey: 'en-US', urlPrefix: '',         hreflang: 'en',    ogLocale: 'en_US' },
  es: { i18nKey: 'es-ES', urlPrefix: '/es',      hreflang: 'es',    ogLocale: 'es_ES' },
  de: { i18nKey: 'de-DE', urlPrefix: '/de',      hreflang: 'de',    ogLocale: 'de_DE' },
  fr: { i18nKey: 'fr-FR', urlPrefix: '/fr',      hreflang: 'fr',    ogLocale: 'fr_FR' },
  ja: { i18nKey: 'ja-JP', urlPrefix: '/ja',      hreflang: 'ja',    ogLocale: 'ja_JP' },
  'pt-BR': { i18nKey: 'pt-BR', urlPrefix: '/pt-BR', hreflang: 'pt-BR', ogLocale: 'pt_BR' },
  'zh-CN': { i18nKey: 'zh-CN', urlPrefix: '/zh-CN', hreflang: 'zh-CN', ogLocale: 'zh_CN' },
  ko: { i18nKey: 'ko-KR', urlPrefix: '/ko',      hreflang: 'ko',    ogLocale: 'ko_KR' },
} as const;

export type SeoLocaleKey = keyof typeof SUPPORTED_SEO_LOCALES;

export const SEO_LOCALE_KEYS: SeoLocaleKey[] = Object.keys(SUPPORTED_SEO_LOCALES) as SeoLocaleKey[];

/**
 * The SEO locale a pathname addresses, taken from its leading URL prefix.
 *
 * Derived from SUPPORTED_SEO_LOCALES rather than a second hand-written list of
 * prefixes, so adding a locale above is enough.
 */
export function seoLocaleKeyFromPathname(pathname: string): SeoLocaleKey {
  const firstSegment = pathname.split('/')[1];
  if (!firstSegment) return 'en';
  const match = SEO_LOCALE_KEYS.find(
    key => SUPPORTED_SEO_LOCALES[key].urlPrefix === `/${firstSegment}`
  );
  return match ?? 'en';
}

/**
 * Rewrites an unprefixed internal path so it stays in the locale `pathname` is in.
 *
 * Needed by any link that is part of a page's own navigation: a visitor reading
 * /de/docs who clicks a section must land on /de/docs/cli, not be dropped back into
 * English mid-journey. Both URLs are prerendered, so the link is a real page either
 * way — this only decides which language the next page speaks.
 */
export function localePrefixedPath(path: string, pathname: string): string {
  const { urlPrefix } = SUPPORTED_SEO_LOCALES[seoLocaleKeyFromPathname(pathname)];
  return `${urlPrefix}${path}`;
}

/**
 * The inverse: a pathname with any locale prefix removed, so it can be compared
 * against the unprefixed paths used in navigation and route tables.
 *
 * Anything deciding "is this the page I link to?" has to strip the prefix first, or
 * it silently answers no for every locale — the header's active-link state did
 * exactly that. Kept here beside the prefix it removes so the two cannot disagree,
 * and so this stays the only place the prefix list is interpreted.
 */
export function stripLocalePrefix(pathname: string): string {
  const { urlPrefix } = SUPPORTED_SEO_LOCALES[seoLocaleKeyFromPathname(pathname)];
  if (!urlPrefix) return pathname;
  const stripped = pathname.slice(urlPrefix.length);
  return stripped === '' ? '/' : stripped;
}

export function absoluteUrl(path: string, localeKey: SeoLocaleKey = 'en'): string {
  const prefix = SUPPORTED_SEO_LOCALES[localeKey].urlPrefix;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (cleanPath === '/') {
    return prefix ? `${CANONICAL_ORIGIN}${prefix}` : CANONICAL_ORIGIN;
  }
  return `${CANONICAL_ORIGIN}${prefix}${cleanPath}`;
}
